import React, { useMemo } from 'react';
import DOMPurify from 'dompurify';
import parse from 'html-react-parser';

/**
 * SafeHtml — secure renderer for admin-authored HTML strings.
 *
 * Pipeline:
 *   raw HTML  ─►  DOMPurify.sanitize()  ─►  html-react-parser.parse()  ─►  React elements
 *
 * No production component (including this wrapper) uses the React
 * raw-HTML injection prop. The sanitized markup is parsed into a real
 * React element tree via html-react-parser, so there is no
 * string-HTML injection sink left in `src/components`.
 *
 * Usage:
 *   <SafeHtml html={template.body_html} className="prose prose-sm" />
 *   <SafeHtml as="span" html={someText} />
 */

// Allowed schemes for hyperlinks. Anything outside this set is dropped
// (so `javascript:` and `data:text/html` URIs in admin-pasted HTML
// can't be used to escalate).
const ALLOWED_URI_REGEXP = /^(?:(?:https?|mailto|tel|ftp):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i;

const SANITIZE_CONFIG = {
  USE_PROFILES: { html: true },
  ALLOWED_URI_REGEXP,
  // Allow target="_blank" + rel="noopener" on anchor tags so external
  // links open in a new tab (existing admin content relies on this).
  ADD_ATTR: ['target', 'rel'],
  // Drop anything dynamic.
  FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form'],
  FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur', 'onchange', 'onsubmit'],
};

export function sanitizeHtml(raw) {
  if (raw == null) return '';
  const s = typeof raw === 'string' ? raw : String(raw);
  return DOMPurify.sanitize(s, SANITIZE_CONFIG);
}

export default function SafeHtml({ html, as: Tag = 'div', className, style, testid, ...rest }) {
  const children = useMemo(() => parse(sanitizeHtml(html)), [html]);
  return (
    <Tag className={className} style={style} data-testid={testid} {...rest}>
      {children}
    </Tag>
  );
}
